/* Turning a drawn canvas into something the PNG and slide exporters can read.
 *
 * The bug this exists for is the surface chart's, and it is the same on every canvas view:
 * `exportHtmlToPng` clones the view's DOM and serialises the clone, and a `<canvas>` survives that
 * trip as an empty element - `recursiveTraversal` in `@ardoq/export` has no case for one - so the
 * export came back blank but for its caption. Reading the canvas directly is the path every other
 * canvas-drawn view takes.
 *
 * The surface needs its own version, in the controller, because it has three stacked layers to
 * flatten and a WebGPU target to blit first. These two charts draw everything on one canvas, so all
 * that is left is the background, the captions and a copy.
 */

import { type MeasureText, wrapCaptions } from './captionBand';

/**
 * The caption band's type scale, in CSS pixels before the canvas ratio is applied.
 *
 * The size and line height of `<Text variant="caption">`, which is what these lines are in the
 * view's header. The stack is the one the rest of this package paints canvas text with
 * (`renderAxes2d`, `renderAxes3d`, `renderSamples`) rather than the product's Inter, so that the
 * band and the axis titles below it are the same type in one exported image.
 */
const CAPTION_FONT_SIZE = 12;
const CAPTION_LINE_HEIGHT = 16;
const CAPTION_PADDING = 8;

const CAPTION_FONT_STACK = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export type ExportCanvasArgs = {
  canvas: HTMLCanvasElement | null;
  backgroundColor: string;
  inkColor: string;
  /**
   * The lines the exported file has to carry that the canvas does not paint.
   *
   * The view's header, in other words. The contour chart names its two axes on the canvas and says
   * what the color means in the DOM above it, and a contour map with no key is a map of an unnamed
   * quantity; the exclusion notice is the same shape with lower stakes. `exportHtmlToPng` used to
   * rasterise both because it rasterised the whole view - which is also why the chart itself came
   * out blank. Reading the canvas fixed the chart and would have dropped these.
   */
  captions: readonly string[];
};

/**
 * The device pixel ratio the canvas was painted at, from the canvas itself.
 *
 * `useChartCanvas` sets the CSS size in layout pixels and the backing store at the ratio, so their
 * quotient is the ratio in force at the last paint - which is the one the picture is drawn at, and
 * not necessarily what `devicePixelRatio` reports now.
 */
const paintedRatio = (canvas: HTMLCanvasElement): number => {
  const cssWidth = canvas.clientWidth;
  if (cssWidth <= 0) return 1;
  return canvas.width / cssWidth;
};

/**
 * Measuring text in a given font, on a canvas kept for nothing else.
 *
 * Null when a 2d context cannot be had, which is not a case worth handling twice: the export canvas
 * below asks for one too, and returns null when it is refused.
 */
const measureTextWith = (font: string): MeasureText | null => {
  const context = new OffscreenCanvas(1, 1).getContext('2d');
  if (!context) return null;

  context.font = font;
  return text => context.measureText(text).width;
};

/**
 * A flattened copy of a chart's canvas, or null when there is nothing to copy.
 *
 * `OffscreenCanvas` rather than an element, matching what `ViewContext.createExportCanvas` declares
 * and what Blocks returns: this never belongs in the document.
 *
 * Whether the chart has painted is the caller's to answer - `useChartCanvas` exposes `hasPainted`
 * for it. It cannot be answered here: an unpainted canvas is not a zero-sized one but a 300x150
 * one, the HTML default backing store, and it is indistinguishable from a small chart.
 */
export const toExportCanvas = ({
  canvas,
  backgroundColor,
  inkColor,
  captions,
}: ExportCanvasArgs): OffscreenCanvas | null => {
  /* `drawImage` of a zero-sized source throws rather than drawing nothing. Reachable only by a
     caller that sizes the canvas itself, which no chart here does. */
  if (!canvas || canvas.width === 0 || canvas.height === 0) return null;

  const ratio = paintedRatio(canvas);
  const font = `${CAPTION_FONT_SIZE * ratio}px ${CAPTION_FONT_STACK}`;

  /* Measured before the export canvas exists, because how many lines the captions take is what its
     height depends on. A canvas of its own to measure on: a context carries the font, and the
     chart's own is not ours to set state on. */
  const measure = captions.length === 0 ? null : measureTextWith(font);
  const lines = measure
    ? wrapCaptions(
        captions,
        canvas.width - CAPTION_PADDING * 2 * ratio,
        measure
      )
    : [];
  const bandHeight =
    lines.length === 0
      ? 0
      : (CAPTION_PADDING * 2 + lines.length * CAPTION_LINE_HEIGHT) * ratio;

  /* Sized from the canvas rather than from `viewport * devicePixelRatio`: the backing store is the
     honest source. A ratio change with no resize leaves the canvas at its old size with CSS scaling
     it, so the chart still looks right while `devicePixelRatio` reports something else, and sizing
     from the current ratio would crop the export or pad it onto white. */
  const exported = new OffscreenCanvas(
    canvas.width,
    canvas.height + bandHeight
  );
  const context = exported.getContext('2d');
  if (!context) return null;

  /* The chart's background is the container's CSS `background`, not anything the canvas draws - it
     is transparent everywhere the chart is not. `pngExporter` composites what it gets onto a
     hard-coded white, which agrees with `surfaceDefault` in the light theme by coincidence and not
     at all in the dark one, where a chart that reads near-white on near-black would export as pale
     ink over `#ffffff`. */
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, exported.width, exported.height);

  context.fillStyle = inkColor;
  context.font = font;
  context.textAlign = 'left';
  context.textBaseline = 'top';
  lines.forEach((line, index) => {
    context.fillText(
      line,
      CAPTION_PADDING * ratio,
      (CAPTION_PADDING + index * CAPTION_LINE_HEIGHT) * ratio
    );
  });

  context.drawImage(canvas, 0, bandHeight);

  return exported;
};
